import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../../services/api.ts";
import { toast } from "react-toastify";
import Navbar from "../navbar/Navbar";
import ProjectForm from "./ProjectForm";
import InviteMemberModal from "./InviteMemberModal";
import StudyForm from "./StudyForm";
import AssignStudyMemberModal from "./AssignStudyMemberModal";
import AssignDatasetToStudyModal from "./AssignDatasetToStudyModal";
import AssignModelToStudyModal from "./AssignModelToStudyModal";
import AssignToolToStudyModal from "./AssignToolToStudyModal";
import AnnotationsSummaryTab from "./AnnotationsSummaryTab";
import ModelRunsSummaryTab from "./ModelRunsSummaryTab";
import ReviewerAnswersSummaryTab from "./ReviewerAnswersSummaryTab";
import AddExistingDatasetModal from "./AddExistingDatasetModal";
import AddExistingModelModal from "./AddExistingModelModal";
import AddExistingToolModal from "./AddExistingToolModal";
import { getModelsOnlyForProject, getToolsForProject, removeModelFromProject } from "../../services/aiModels";
import "./Projects.css";
import "../shared/starfield.css";

const ProjectDetail = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [members, setMembers] = useState({ owner: null, members: [] });
    const [studies, setStudies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingStudies, setLoadingStudies] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showStudyModal, setShowStudyModal] = useState(false);
    const [editingStudy, setEditingStudy] = useState(null);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedStudyForAssign, setSelectedStudyForAssign] = useState(null);
    const [expandedStudy, setExpandedStudy] = useState(null);
    // Tracks the "{studyId}-{datasetId}" currently being unassigned so we can
    // spinner + disable the button. Null when idle.
    const [unassigningDataset, setUnassigningDataset] = useState(null);
    const [activeTab, setActiveTab] = useState("overview");
    const [datasets, setDatasets] = useState([]);
    const [loadingDatasets, setLoadingDatasets] = useState(false);
    const [showAssignDatasetModal, setShowAssignDatasetModal] = useState(false);
    const [selectedDatasetForAssign, setSelectedDatasetForAssign] = useState(null);
    const [annotations, setAnnotations] = useState([]);
    const [annotationSummary, setAnnotationSummary] = useState(null);
    const [loadingAnnotations, setLoadingAnnotations] = useState(false);
    const [annotationFilters, setAnnotationFilters] = useState({ studyId: '' });
    const [modelRuns, setModelRuns] = useState([]);
    const [modelRunsSummary, setModelRunsSummary] = useState(null);
    const [loadingModelRuns, setLoadingModelRuns] = useState(false);
    const [modelRunsFilters, setModelRunsFilters] = useState({ studyId: '' });
    const [reviewerAnswers, setReviewerAnswers] = useState([]);
    const [reviewerAnswersSummary, setReviewerAnswersSummary] = useState(null);
    const [loadingReviewerAnswers, setLoadingReviewerAnswers] = useState(false);
    const [reviewerAnswersFilters, setReviewerAnswersFilters] = useState({ studyId: '' });
    const [models, setModels] = useState([]);
    const [loadingModels, setLoadingModels] = useState(false);
    const [showAssignModelModal, setShowAssignModelModal] = useState(false);
    const [selectedModelForAssign, setSelectedModelForAssign] = useState(null);
    const [showAddDatasetModal, setShowAddDatasetModal] = useState(false);
    const [showAddModelModal, setShowAddModelModal] = useState(false);
    const [tools, setTools] = useState([]);
    const [loadingTools, setLoadingTools] = useState(false);
    const [showAssignToolModal, setShowAssignToolModal] = useState(false);
    const [selectedToolForAssign, setSelectedToolForAssign] = useState(null);
    const [showAddToolModal, setShowAddToolModal] = useState(false);

    const currentUsername = localStorage.getItem("username");

    const fetchProject = useCallback(async () => {
        try {
            setLoading(true);
            const response = await API.get(`/projects/${projectId}/`);
            setProject(response.data);
        } catch (err) {
            console.error("Error fetching project:", err);
            toast.error("Failed to fetch project.");
            navigate("/Projects");
        } finally {
            setLoading(false);
        }
    }, [projectId, navigate]);

    const fetchMembers = useCallback(async () => {
        try {
            const response = await API.get(`/projects/${projectId}/members/`);
            setMembers(response.data);
        } catch (err) {
            console.error("Error fetching members:", err);
        }
    }, [projectId]);

    const fetchStudies = useCallback(async () => {
        try {
            setLoadingStudies(true);
            const response = await API.get(`/studies/?project=${projectId}`);
            setStudies(response.data);
        } catch (err) {
            console.error("Error fetching studies:", err);
        } finally {
            setLoadingStudies(false);
        }
    }, [projectId]);

    const fetchDatasets = useCallback(async () => {
        try {
            setLoadingDatasets(true);
            const response = await API.get(`/datasets/?project=${projectId}`);
            setDatasets(response.data);
        } catch (err) {
            console.error("Error fetching datasets:", err);
        } finally {
            setLoadingDatasets(false);
        }
    }, [projectId]);

    const fetchAnnotations = useCallback(async () => {
        try {
            setLoadingAnnotations(true);
            const params = new URLSearchParams();
            if (annotationFilters.studyId) params.append('study_id', annotationFilters.studyId);

            const response = await API.get(`/projects/${projectId}/annotations/?${params.toString()}`);
            setAnnotations(response.data.annotations || []);
            setAnnotationSummary(response.data.summary || null);
        } catch (err) {
            console.error("Error fetching annotations:", err);
            toast.error("Failed to fetch annotations.");
        } finally {
            setLoadingAnnotations(false);
        }
    }, [projectId, annotationFilters]);

    const fetchModelRuns = useCallback(async () => {
        try {
            setLoadingModelRuns(true);
            const params = new URLSearchParams();
            if (modelRunsFilters.studyId) params.append('study_id', modelRunsFilters.studyId);

            const response = await API.get(`/projects/${projectId}/model-runs/?${params.toString()}`);
            setModelRuns(response.data.runs || []);
            setModelRunsSummary(response.data.summary || null);
        } catch (err) {
            console.error("Error fetching model runs:", err);
            toast.error("Failed to fetch model runs.");
        } finally {
            setLoadingModelRuns(false);
        }
    }, [projectId, modelRunsFilters]);

    const fetchReviewerAnswers = useCallback(async () => {
        try {
            setLoadingReviewerAnswers(true);
            const params = new URLSearchParams();
            if (reviewerAnswersFilters.studyId) params.append('study_id', reviewerAnswersFilters.studyId);

            const response = await API.get(`/projects/${projectId}/reviewer-answers/?${params.toString()}`);
            setReviewerAnswers(response.data.submissions || []);
            setReviewerAnswersSummary(response.data.summary || null);
        } catch (err) {
            console.error("Error fetching reviewer answers:", err);
            toast.error("Failed to fetch reviewer answers.");
        } finally {
            setLoadingReviewerAnswers(false);
        }
    }, [projectId, reviewerAnswersFilters]);

    const fetchModels = useCallback(async () => {
        try {
            setLoadingModels(true);
            const data = await getModelsOnlyForProject(projectId);
            setModels(data);
        } catch (err) {
            console.error("Error fetching models:", err);
        } finally {
            setLoadingModels(false);
        }
    }, [projectId]);

    const fetchTools = useCallback(async () => {
        try {
            setLoadingTools(true);
            const data = await getToolsForProject(projectId);
            setTools(data);
        } catch (err) {
            console.error("Error fetching tools:", err);
        } finally {
            setLoadingTools(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchProject();
        fetchMembers();
    }, [fetchProject, fetchMembers]);

    useEffect(() => {
        if (activeTab === "studies") {
            fetchStudies();
        }
        if (activeTab === "datasets") {
            fetchDatasets();
            fetchStudies(); // Also fetch studies for "Assign to Study" functionality
        }
        if (activeTab === "annotations") {
            fetchAnnotations();
            if (studies.length === 0) fetchStudies(); // For filter dropdown
        }
        if (activeTab === "model_runs") {
            fetchModelRuns();
            if (studies.length === 0) fetchStudies(); // For filter dropdown
        }
        if (activeTab === "reviewer_answers") {
            fetchReviewerAnswers();
            if (studies.length === 0) fetchStudies(); // For filter dropdown
        }
        if (activeTab === "models") {
            fetchModels();
            fetchStudies(); // Also fetch studies for "Assign to Study" functionality
        }
        if (activeTab === "tools") {
            fetchTools();
            fetchStudies(); // Also fetch studies for "Assign to Study" functionality
        }
    }, [activeTab, fetchStudies, fetchDatasets, fetchAnnotations, fetchModelRuns, fetchReviewerAnswers, fetchModels, fetchTools, studies.length]);

    const handleDeleteProject = async () => {
        if (!window.confirm("Are you sure you want to delete this project? This action cannot be undone.")) {
            return;
        }

        try {
            await API.delete(`/projects/${projectId}/`);
            toast.success("Project deleted successfully.");
            navigate("/Projects");
        } catch (err) {
            console.error("Error deleting project:", err);
            toast.error("Failed to delete project.");
        }
    };

    const handleLeaveProject = async () => {
        if (!window.confirm("Are you sure you want to leave this project?")) {
            return;
        }

        try {
            await API.post(`/projects/${projectId}/leave/`);
            toast.success("Left project successfully.");
            navigate("/Projects");
        } catch (err) {
            console.error("Error leaving project:", err);
            toast.error(err.response?.data?.error || "Failed to leave project.");
        }
    };

    const handleRemoveMember = async (userId, username) => {
        if (!window.confirm(`Are you sure you want to remove ${username} from this project?`)) {
            return;
        }

        try {
            await API.delete(`/projects/${projectId}/members/${userId}/`);
            toast.success(`${username} removed from project.`);
            fetchMembers();
            fetchProject();
        } catch (err) {
            console.error("Error removing member:", err);
            toast.error("Failed to remove member.");
        }
    };

    const handleUpdateRole = async (userId, newRole) => {
        try {
            await API.patch(`/projects/${projectId}/members/${userId}/role/`, {
                role: newRole,
            });
            toast.success("Role updated successfully.");
            fetchMembers();
        } catch (err) {
            console.error("Error updating role:", err);
            toast.error("Failed to update role.");
        }
    };

    const handleDeleteStudy = async (studyId, studyName) => {
        if (!window.confirm(`Are you sure you want to delete the study "${studyName}"?`)) {
            return;
        }

        try {
            await API.delete(`/studies/${studyId}/`);
            toast.success("Study deleted successfully.");
            fetchStudies();
            fetchProject();
        } catch (err) {
            console.error("Error deleting study:", err);
            toast.error("Failed to delete study.");
        }
    };

    const handleUnassignDataset = async (study, dataset) => {
        const label = `"${dataset.name}" from "${study.name}"`;
        if (!window.confirm(
            `Unassign ${label}?\n\n`
            + `Reviewer patient assignments in this study that point at "${dataset.name}"'s `
            + `patients will be removed. This can't be undone.`
        )) return;
        const key = `${study.id}-${dataset.id}`;
        try {
            setUnassigningDataset(key);
            await API.post(`/studies/${study.id}/remove_dataset/`, { dataset_id: dataset.id });
            toast.success("Dataset unassigned.");
            fetchStudies();
        } catch (err) {
            console.error("Error unassigning dataset:", err);
            toast.error(err.response?.data?.detail || "Failed to unassign dataset.");
        } finally {
            setUnassigningDataset(null);
        }
    };

    const handleUpdateStudyStatus = async (studyId, newStatus) => {
        try {
            await API.patch(`/studies/${studyId}/status/`, { status: newStatus });
            toast.success("Study status updated.");
            fetchStudies();
        } catch (err) {
            console.error("Error updating study status:", err);
            toast.error("Failed to update status.");
        }
    };

    const handleRemoveStudyAssignment = async (studyId, userId, username) => {
        if (!window.confirm(`Remove ${username} from this study?`)) {
            return;
        }

        try {
            await API.delete(`/studies/${studyId}/assignments/${userId}/`);
            toast.success("Assignment removed.");
            fetchStudies();
        } catch (err) {
            console.error("Error removing assignment:", err);
            toast.error("Failed to remove assignment.");
        }
    };

    const handleUpdateAssignmentRole = async (studyId, userId, newRole) => {
        try {
            await API.patch(`/studies/${studyId}/assignments/${userId}/role/`, { role: newRole });
            toast.success("Assignment role updated.");
            fetchStudies();
        } catch (err) {
            console.error("Error updating assignment role:", err);
            toast.error("Failed to update role.");
        }
    };

    const openStudyInViewer = (studyId) => {
        navigate(`/Study/${studyId}`);
    };

    // Check if current user can access a study (assigned to it, created it, or is project owner/admin)
    const canAccessStudy = (study) => {
        console.log('canAccessStudy check:', {
            studyId: study.id,
            currentUsername,
            isOwner,
            isAdmin,
            created_by: study.created_by_user?.username,
            assignments: study.assignments?.map(a => a.user?.username)
        });
        // Project owner can access all studies
        if (isOwner) return true;
        // Project admin can access all studies
        if (isAdmin) return true;
        // Study creator can access
        if (study.created_by_user?.username === currentUsername) return true;
        // Check if user is assigned to the study
        const isAssigned = study.assignments?.some(a => a.user?.username === currentUsername);
        console.log('isAssigned result:', isAssigned);
        return isAssigned;
    };

    const handleRemoveModelFromProject = async (model) => {
        if (!window.confirm(`Remove "${model.name}" from this project?`)) {
            return;
        }

        try {
            await removeModelFromProject(model.id, projectId);
            toast.success(`Model "${model.name}" removed from project.`);
            fetchModels();
            fetchProject();
        } catch (err) {
            console.error("Error removing model:", err);
            toast.error("Failed to remove model from project.");
        }
    };

    const handleRemoveToolFromProject = async (tool) => {
        if (!window.confirm(`Remove "${tool.name}" from this project?`)) {
            return;
        }

        try {
            await removeModelFromProject(tool.id, projectId);
            toast.success(`Tool "${tool.name}" removed from project.`);
            fetchTools();
            fetchProject();
        } catch (err) {
            console.error("Error removing tool:", err);
            toast.error("Failed to remove tool from project.");
        }
    };

    const getBuildStatusBadge = (buildStatus) => {
        const statusConfig = {
            pending: { class: 'bg-secondary', label: 'Pending' },
            building: { class: 'bg-info', label: 'Building' },
            ready: { class: 'bg-success', label: 'Ready' },
            failed: { class: 'bg-danger', label: 'Failed' },
        };
        const config = statusConfig[buildStatus] || statusConfig.pending;
        return <span className={`badge ${config.class}`}>{config.label}</span>;
    };

    // Check ownership via both username comparison AND user_role field
    // The backend returns user_role="owner" when the user is the project owner
    const isOwner = project?.owner?.username === currentUsername || project?.user_role === "owner";
    const isAdmin = project?.user_role === "admin" || isOwner;
    const canEdit = project?.user_role === "editor" || isAdmin;
    const isViewer = project?.user_role === "viewer";

    const getRoleBadge = (role) => {
        const colors = {
            owner: "bg-primary",
            admin: "bg-warning text-dark",
            editor: "bg-success",
            viewer: "bg-secondary",
        };
        return <span className={`badge ${colors[role] || "bg-secondary"}`}>{role}</span>;
    };

    const getStatusBadge = (status) => {
        const colors = {
            draft: "bg-secondary",
            active: "bg-primary",
            completed: "bg-success",
            archived: "bg-dark",
        };
        return <span className={`badge ${colors[status] || "bg-secondary"}`}>{status}</span>;
    };

    const getAssignmentRoleBadge = (role) => {
        const colors = {
            lead: "bg-danger",
            contributor: "bg-info",
            reviewer: "bg-warning text-dark",
        };
        return <span className={`badge ${colors[role] || "bg-secondary"}`}>{role}</span>;
    };

    if (loading) {
        return (
            <div className="projects-page">
                <Navbar />
                <div className="container py-5 text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="projects-page">
                <Navbar />
                <div className="container py-5 text-center">
                    <h3>Project not found</h3>
                    <button className="btn btn-primary mt-3" onClick={() => navigate("/Projects")}>
                        Back to Projects
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="projects-page">
            {/* Starfield Background */}
            <div className="starfield-background"></div>

            <Navbar />
            <div className="container py-4">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <a href="/Projects">Projects</a>
                                </li>
                                <li className="breadcrumb-item active">{project.name}</li>
                            </ol>
                        </nav>
                        <h2 className="mb-1">
                            {project.name}
                            {project.visibility === "public" && (
                                <span className="badge bg-info ms-2">Public</span>
                            )}
                        </h2>
                        <p className="text-muted">{project.description || "No description"}</p>
                    </div>
                    <div className="d-flex gap-2">
                        {isOwner && (
                            <>
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={() => setShowEditModal(true)}
                                >
                                    Edit
                                </button>
                                <button
                                    className="btn btn-outline-danger"
                                    onClick={handleDeleteProject}
                                >
                                    Delete
                                </button>
                            </>
                        )}
                        {!isOwner && (
                            <button
                                className="btn btn-outline-warning"
                                onClick={handleLeaveProject}
                            >
                                Leave Project
                            </button>
                        )}
                    </div>
                </div>

                {/* Stats */}
                <div className="row mb-4">
                    <div className="col-md-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <h3>{project.member_count}</h3>
                                <p className="text-muted mb-0">Members</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <h3>{project.study_count}</h3>
                                <p className="text-muted mb-0">Studies</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <h3>{project.dataset_count}</h3>
                                <p className="text-muted mb-0">Datasets</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card text-center">
                            <div className="card-body">
                                <h3>{getRoleBadge(project.user_role)}</h3>
                                <p className="text-muted mb-0">Your Role</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <ul className="nav nav-tabs mb-4">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === "overview" ? "active" : ""}`}
                            onClick={() => setActiveTab("overview")}
                        >
                            Overview
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === "members" ? "active" : ""}`}
                            onClick={() => setActiveTab("members")}
                        >
                            Members ({project.member_count})
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === "studies" ? "active" : ""}`}
                            onClick={() => setActiveTab("studies")}
                        >
                            Studies ({project.study_count})
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === "datasets" ? "active" : ""}`}
                            onClick={() => setActiveTab("datasets")}
                        >
                            Datasets ({project.dataset_count})
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === "models" ? "active" : ""}`}
                            onClick={() => setActiveTab("models")}
                        >
                            AI Models ({models.length})
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === "tools" ? "active" : ""}`}
                            onClick={() => setActiveTab("tools")}
                        >
                            Tools ({tools.length})
                        </button>
                    </li>
                    {/* Hide Annotations tab for Viewers - they should not see other users' annotations */}
                    {!isViewer && (
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === "annotations" ? "active" : ""}`}
                                onClick={() => setActiveTab("annotations")}
                            >
                                Annotations
                            </button>
                        </li>
                    )}
                    {/* Model Runs tab — admin/contributor view of every AI panel
                        execution kicked off inside this project's studies.
                        Hidden from plain viewers for the same privacy reasons
                        as Annotations. */}
                    {!isViewer && (
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === "model_runs" ? "active" : ""}`}
                                onClick={() => setActiveTab("model_runs")}
                            >
                                Model Runs
                            </button>
                        </li>
                    )}
                    {/* Reviewer Answers — text-field answers submitted by
                        reviewers inside scrollable-layout viewers. */}
                    {!isViewer && (
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === "reviewer_answers" ? "active" : ""}`}
                                onClick={() => setActiveTab("reviewer_answers")}
                            >
                                Reviewer Answers
                            </button>
                        </li>
                    )}
                </ul>

                {/* Tab Content */}
                {activeTab === "overview" && (
                    <div className="card">
                        <div className="card-body">
                            <h5>Project Details</h5>
                            <table className="table table-borderless">
                                <tbody>
                                    <tr>
                                        <th style={{ width: "150px" }}>Owner</th>
                                        <td>{project.owner?.username}</td>
                                    </tr>
                                    <tr>
                                        <th>Created</th>
                                        <td>{new Date(project.created_at).toLocaleDateString()}</td>
                                    </tr>
                                    <tr>
                                        <th>Last Updated</th>
                                        <td>{new Date(project.updated_at).toLocaleDateString()}</td>
                                    </tr>
                                    <tr>
                                        <th>Visibility</th>
                                        <td>
                                            {project.visibility === "public" ? "Public" : "Private"}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === "members" && (
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Team Members</h5>
                            {isAdmin && (
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setShowInviteModal(true)}
                                >
                                    + Invite Member
                                </button>
                            )}
                        </div>
                        <div className="card-body">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Role</th>
                                        <th>Joined</th>
                                        {isAdmin && <th>Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Owner */}
                                    {members.owner && (
                                        <tr>
                                            <td>
                                                <strong>{members.owner.user.username}</strong>
                                                {members.owner.user.first_name && (
                                                    <span className="text-muted ms-2">
                                                        ({members.owner.user.first_name}{" "}
                                                        {members.owner.user.last_name})
                                                    </span>
                                                )}
                                            </td>
                                            <td>{getRoleBadge("owner")}</td>
                                            <td>
                                                {new Date(
                                                    members.owner.invited_at
                                                ).toLocaleDateString()}
                                            </td>
                                            {isAdmin && <td>-</td>}
                                        </tr>
                                    )}
                                    {/* Other members */}
                                    {members.members
                                        .filter((m) => m.status === "accepted")
                                        .map((member) => (
                                            <tr key={member.id}>
                                                <td>
                                                    <strong>{member.user.username}</strong>
                                                    {member.user.first_name && (
                                                        <span className="text-muted ms-2">
                                                            ({member.user.first_name}{" "}
                                                            {member.user.last_name})
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {isAdmin ? (
                                                        <select
                                                            className="form-select form-select-sm"
                                                            style={{ width: "auto" }}
                                                            value={member.role}
                                                            onChange={(e) =>
                                                                handleUpdateRole(
                                                                    member.user.id,
                                                                    e.target.value
                                                                )
                                                            }
                                                        >
                                                            <option value="viewer">Viewer</option>
                                                            <option value="editor">Editor</option>
                                                            <option value="admin">Admin</option>
                                                        </select>
                                                    ) : (
                                                        getRoleBadge(member.role)
                                                    )}
                                                </td>
                                                <td>
                                                    {member.accepted_at
                                                        ? new Date(
                                                              member.accepted_at
                                                          ).toLocaleDateString()
                                                        : "-"}
                                                </td>
                                                {isAdmin && (
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() =>
                                                                handleRemoveMember(
                                                                    member.user.id,
                                                                    member.user.username
                                                                )
                                                            }
                                                        >
                                                            Remove
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    {/* Pending invitations */}
                                    {members.members
                                        .filter((m) => m.status === "pending")
                                        .map((member) => (
                                            <tr key={member.id} className="table-warning">
                                                <td>
                                                    <strong>{member.user.username}</strong>
                                                    <span className="badge bg-warning text-dark ms-2">
                                                        Pending
                                                    </span>
                                                </td>
                                                <td>{getRoleBadge(member.role)}</td>
                                                <td>Invited {new Date(member.invited_at).toLocaleDateString()}</td>
                                                {isAdmin && (
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() =>
                                                                handleRemoveMember(
                                                                    member.user.id,
                                                                    member.user.username
                                                                )
                                                            }
                                                        >
                                                            Cancel
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === "studies" && (
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Project Studies</h5>
                            {canEdit && (
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => {
                                        setEditingStudy(null);
                                        setShowStudyModal(true);
                                    }}
                                >
                                    + Create Study
                                </button>
                            )}
                        </div>
                        <div className="card-body">
                            {loadingStudies ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : studies.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <p>No studies in this project yet.</p>
                                    {canEdit && (
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={() => {
                                                setEditingStudy(null);
                                                setShowStudyModal(true);
                                            }}
                                        >
                                            Create your first study
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="accordion" id="studiesAccordion">
                                    {studies.map((study) => (
                                        <div className="accordion-item" key={study.id}>
                                            <h2 className="accordion-header">
                                                <button
                                                    className={`accordion-button ${expandedStudy === study.id ? "" : "collapsed"}`}
                                                    type="button"
                                                    onClick={() => setExpandedStudy(expandedStudy === study.id ? null : study.id)}
                                                >
                                                    <div className="d-flex align-items-center gap-3 w-100">
                                                        <span className="fw-bold">{study.name}</span>
                                                        {getStatusBadge(study.status)}
                                                        <span className="text-muted small">
                                                            <i className="fa fa-database me-1"></i>
                                                            {study.dataset_count} datasets
                                                        </span>
                                                        <span className="text-muted small">
                                                            <i className="fa fa-users me-1"></i>
                                                            {study.assignment_count} assigned
                                                        </span>
                                                    </div>
                                                </button>
                                            </h2>
                                            <div className={`accordion-collapse collapse ${expandedStudy === study.id ? "show" : ""}`}>
                                                <div className="accordion-body">
                                                    <div className="row">
                                                        <div className="col-md-8">
                                                            <p>{study.description || "No description"}</p>
                                                            <small className="text-muted">
                                                                Created by {study.created_by_user?.username || "Unknown"} on{" "}
                                                                {new Date(study.created_at).toLocaleDateString()}
                                                            </small>

                                                            {/* Datasets Section */}
                                                            <div className="mt-3">
                                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                                    <h6 className="mb-0">Assigned Datasets</h6>
                                                                </div>
                                                                {study.datasets_detail?.length > 0 ? (
                                                                    <table className="table table-sm">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Name</th>
                                                                                <th>Patients</th>
                                                                                {canEdit && <th>Actions</th>}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {study.datasets_detail.map((dataset) => (
                                                                                <tr key={dataset.id}>
                                                                                    <td>
                                                                                        <strong>{dataset.name}</strong>
                                                                                        {dataset.description && (
                                                                                            <small className="d-block text-muted">{dataset.description}</small>
                                                                                        )}
                                                                                    </td>
                                                                                    <td>
                                                                                        <span className="badge bg-secondary">
                                                                                            {dataset.patient_count}
                                                                                        </span>
                                                                                    </td>
                                                                                    {canEdit && (
                                                                                        <td>
                                                                                            <div className="d-flex gap-1">
                                                                                                <button
                                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                                    onClick={() => navigate(`/Datasets/${dataset.id}`)}
                                                                                                    title="View Dataset"
                                                                                                >
                                                                                                    <i className="fa fa-eye"></i>
                                                                                                </button>
                                                                                                <button
                                                                                                    className="btn btn-sm btn-outline-danger"
                                                                                                    onClick={() => handleUnassignDataset(study, dataset)}
                                                                                                    disabled={unassigningDataset === `${study.id}-${dataset.id}`}
                                                                                                    title="Unassign dataset from study"
                                                                                                >
                                                                                                    {unassigningDataset === `${study.id}-${dataset.id}` ? (
                                                                                                        <span className="spinner-border spinner-border-sm"></span>
                                                                                                    ) : (
                                                                                                        <i className="fa fa-unlink"></i>
                                                                                                    )}
                                                                                                </button>
                                                                                            </div>
                                                                                        </td>
                                                                                    )}
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                ) : (
                                                                    <p className="text-muted small">No datasets assigned yet.</p>
                                                                )}
                                                            </div>

                                                            {/* Assignments Section */}
                                                            <div className="mt-3">
                                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                                    <h6 className="mb-0">Assigned Members</h6>
                                                                    {canEdit && (
                                                                        <button
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            onClick={() => {
                                                                                setSelectedStudyForAssign(study);
                                                                                setShowAssignModal(true);
                                                                            }}
                                                                        >
                                                                            + Assign
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {study.assignments?.length > 0 ? (
                                                                    <table className="table table-sm">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>User</th>
                                                                                <th>Role</th>
                                                                                {canEdit && <th>Actions</th>}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {study.assignments.map((assignment) => (
                                                                                <tr key={assignment.id}>
                                                                                    <td>{assignment.user?.username}</td>
                                                                                    <td>
                                                                                        <span className="badge bg-warning text-dark">Reviewer</span>
                                                                                    </td>
                                                                                    {canEdit && (
                                                                                        <td>
                                                                                            <div className="d-flex gap-1">
                                                                                                <button
                                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                                    onClick={() =>
                                                                                                        navigate(`/Study/${study.id}?view_as=${assignment.user.id}`)
                                                                                                    }
                                                                                                    title={`View study as ${assignment.user.username}`}
                                                                                                >
                                                                                                    <i className="fa fa-eye"></i>
                                                                                                </button>
                                                                                                <button
                                                                                                    className="btn btn-sm btn-outline-danger"
                                                                                                    onClick={() =>
                                                                                                        handleRemoveStudyAssignment(
                                                                                                            study.id,
                                                                                                            assignment.user.id,
                                                                                                            assignment.user.username
                                                                                                        )
                                                                                                    }
                                                                                                >
                                                                                                    Remove
                                                                                                </button>
                                                                                            </div>
                                                                                        </td>
                                                                                    )}
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                ) : (
                                                                    <p className="text-muted small">No members assigned yet.</p>
                                                                )}
                                                            </div>

                                                            {/* AI Models Section */}
                                                            <div className="mt-3">
                                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                                    <h6 className="mb-0">Assigned AI Models</h6>
                                                                </div>
                                                                {study.models_detail?.filter(m => !m.entity_type || m.entity_type === 'model').length > 0 ? (
                                                                    <div className="d-flex flex-wrap gap-2">
                                                                        {study.models_detail.filter(m => !m.entity_type || m.entity_type === 'model').map((model) => (
                                                                            <span
                                                                                key={model.id}
                                                                                className="badge bg-light text-dark border d-flex align-items-center gap-1"
                                                                                style={{ fontSize: '0.85rem' }}
                                                                            >
                                                                                <i
                                                                                    className={`fa ${model.icon || 'fa-cube'}`}
                                                                                    style={{ color: model.color || '#6f42c1' }}
                                                                                ></i>
                                                                                {model.name}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-muted small">No AI models assigned yet.</p>
                                                                )}
                                                            </div>

                                                            {/* Tools Section */}
                                                            <div className="mt-3">
                                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                                    <h6 className="mb-0">Assigned Tools</h6>
                                                                </div>
                                                                {study.models_detail?.filter(m => m.entity_type === 'tool').length > 0 ? (
                                                                    <div className="d-flex flex-wrap gap-2">
                                                                        {study.models_detail.filter(m => m.entity_type === 'tool').map((tool) => (
                                                                            <span
                                                                                key={tool.id}
                                                                                className="badge bg-light text-dark border d-flex align-items-center gap-1"
                                                                                style={{ fontSize: '0.85rem' }}
                                                                            >
                                                                                <i
                                                                                    className={`fa ${tool.icon || 'fa-wrench'}`}
                                                                                    style={{ color: tool.color || '#6c757d' }}
                                                                                ></i>
                                                                                {tool.name}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-muted small">No tools assigned yet.</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <div className="d-flex flex-column gap-2">
                                                                {canAccessStudy(study) ? (
                                                                    <button
                                                                        className="btn btn-primary"
                                                                        onClick={() => openStudyInViewer(study.id)}
                                                                    >
                                                                        <i className="fa fa-eye me-2"></i>
                                                                        Open Study
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        className="btn btn-secondary"
                                                                        disabled
                                                                        title="You need to be assigned to this study to open it"
                                                                    >
                                                                        <i className="fa fa-lock me-2"></i>
                                                                        Not Assigned
                                                                    </button>
                                                                )}
                                                                {canEdit && (
                                                                    <>
                                                                        <button
                                                                            className="btn btn-outline-secondary"
                                                                            onClick={() => {
                                                                                setEditingStudy(study);
                                                                                setShowStudyModal(true);
                                                                            }}
                                                                        >
                                                                            <i className="fa fa-edit me-2"></i>
                                                                            Edit Study
                                                                        </button>
                                                                        <div className="btn-group">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-secondary dropdown-toggle"
                                                                                data-bs-toggle="dropdown"
                                                                            >
                                                                                Status: {study.status}
                                                                            </button>
                                                                            <ul className="dropdown-menu">
                                                                                <li>
                                                                                    <button
                                                                                        className="dropdown-item"
                                                                                        onClick={() => handleUpdateStudyStatus(study.id, "draft")}
                                                                                    >
                                                                                        Draft
                                                                                    </button>
                                                                                </li>
                                                                                <li>
                                                                                    <button
                                                                                        className="dropdown-item"
                                                                                        onClick={() => handleUpdateStudyStatus(study.id, "active")}
                                                                                    >
                                                                                        Active
                                                                                    </button>
                                                                                </li>
                                                                                <li>
                                                                                    <button
                                                                                        className="dropdown-item"
                                                                                        onClick={() => handleUpdateStudyStatus(study.id, "completed")}
                                                                                    >
                                                                                        Completed
                                                                                    </button>
                                                                                </li>
                                                                                <li>
                                                                                    <button
                                                                                        className="dropdown-item"
                                                                                        onClick={() => handleUpdateStudyStatus(study.id, "archived")}
                                                                                    >
                                                                                        Archived
                                                                                    </button>
                                                                                </li>
                                                                            </ul>
                                                                        </div>
                                                                        <button
                                                                            className="btn btn-outline-danger"
                                                                            onClick={() => handleDeleteStudy(study.id, study.name)}
                                                                        >
                                                                            <i className="fa fa-trash me-2"></i>
                                                                            Delete Study
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === "datasets" && (
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Project Datasets</h5>
                            {canEdit && (
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() => setShowAddDatasetModal(true)}
                                    >
                                        <i className="fa fa-plus me-1"></i>
                                        Add Existing Dataset
                                    </button>
                                    <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => navigate("/Datasets")}
                                    >
                                        <i className="fa fa-external-link me-1"></i>
                                        Manage All Datasets
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="card-body">
                            {loadingDatasets ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : datasets.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="fa fa-database mb-3" style={{ fontSize: "2.5rem", opacity: 0.5 }}></i>
                                    <p>No datasets assigned to this project yet.</p>
                                    {canEdit && (
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={() => navigate("/Datasets")}
                                        >
                                            Go to Datasets to assign one
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Description</th>
                                            <th>Patients</th>
                                            <th>Created</th>
                                            {canEdit && <th>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {datasets.map((dataset) => (
                                            <tr key={dataset.id}>
                                                <td>
                                                    <strong>{dataset.name}</strong>
                                                </td>
                                                <td className="text-muted" style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {dataset.description || "-"}
                                                </td>
                                                <td>
                                                    <span className="badge bg-secondary">
                                                        {dataset.patient_count || dataset.patients?.length || 0}
                                                    </span>
                                                </td>
                                                <td>{new Date(dataset.created_at).toLocaleDateString()}</td>
                                                {canEdit && (
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => navigate(`/Datasets/${dataset.id}`)}
                                                                title="View Dataset"
                                                            >
                                                                <i className="fa fa-eye"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-success"
                                                                onClick={() => {
                                                                    setSelectedDatasetForAssign(dataset);
                                                                    setShowAssignDatasetModal(true);
                                                                }}
                                                                title="Assign to Study"
                                                            >
                                                                <i className="fa fa-book"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === "models" && (
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Project AI Models</h5>
                            {canEdit && (
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() => setShowAddModelModal(true)}
                                    >
                                        <i className="fa fa-plus me-1"></i>
                                        Add Existing Model
                                    </button>
                                    <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => navigate("/Models")}
                                    >
                                        <i className="fa fa-external-link me-1"></i>
                                        Manage All Models
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="card-body">
                            {loadingModels ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : models.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="fa fa-cube mb-3" style={{ fontSize: "2.5rem", opacity: 0.5 }}></i>
                                    <p>No AI models assigned to this project yet.</p>
                                    {canEdit && (
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={() => navigate("/Models")}
                                        >
                                            Go to Models to assign one
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Model</th>
                                            <th>Type</th>
                                            <th>Build Status</th>
                                            <th>Created</th>
                                            {canEdit && <th>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {models.map((model) => (
                                            <tr key={model.id}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i
                                                            className={`fa ${model.icon || 'fa-cube'}`}
                                                            style={{ color: model.color || '#6f42c1', fontSize: '1.25rem' }}
                                                        ></i>
                                                        <div>
                                                            <strong>{model.name}</strong>
                                                            {model.description && (
                                                                <small className="d-block text-muted" style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                    {model.description}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{model.model_type_display || model.model_type || "-"}</td>
                                                <td>{getBuildStatusBadge(model.build_status)}</td>
                                                <td>{new Date(model.created_at).toLocaleDateString()}</td>
                                                {canEdit && (
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                className="btn btn-sm btn-outline-success"
                                                                onClick={() => {
                                                                    setSelectedModelForAssign(model);
                                                                    setShowAssignModelModal(true);
                                                                }}
                                                                title="Assign to Study"
                                                            >
                                                                <i className="fa fa-book"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleRemoveModelFromProject(model)}
                                                                title="Remove from Project"
                                                            >
                                                                <i className="fa fa-times"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === "tools" && (
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Project Tools</h5>
                            {canEdit && (
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() => setShowAddToolModal(true)}
                                    >
                                        <i className="fa fa-plus me-1"></i>
                                        Add Existing Tool
                                    </button>
                                    <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => navigate("/Models")}
                                    >
                                        <i className="fa fa-external-link me-1"></i>
                                        Manage All Tools
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="card-body">
                            {loadingTools ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : tools.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="fa fa-wrench mb-3" style={{ fontSize: "2.5rem", opacity: 0.5 }}></i>
                                    <p>No tools assigned to this project yet.</p>
                                    {canEdit && (
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={() => navigate("/Models")}
                                        >
                                            Go to Models & Tools to assign one
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Tool</th>
                                            <th>Type</th>
                                            <th>Build Status</th>
                                            <th>Created</th>
                                            {canEdit && <th>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tools.map((tool) => (
                                            <tr key={tool.id}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i
                                                            className={`fa ${tool.icon || 'fa-wrench'}`}
                                                            style={{ color: tool.color || '#6c757d', fontSize: '1.25rem' }}
                                                        ></i>
                                                        <div>
                                                            <strong>{tool.name}</strong>
                                                            {tool.description && (
                                                                <small className="d-block text-muted" style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                    {tool.description}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{tool.model_type_display || tool.model_type || "-"}</td>
                                                <td>{getBuildStatusBadge(tool.build_status)}</td>
                                                <td>{new Date(tool.created_at).toLocaleDateString()}</td>
                                                {canEdit && (
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                className="btn btn-sm btn-outline-success"
                                                                onClick={() => {
                                                                    setSelectedToolForAssign(tool);
                                                                    setShowAssignToolModal(true);
                                                                }}
                                                                title="Assign to Study"
                                                            >
                                                                <i className="fa fa-book"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleRemoveToolFromProject(tool)}
                                                                title="Remove from Project"
                                                            >
                                                                <i className="fa fa-times"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === "annotations" && !isViewer && (
                    <AnnotationsSummaryTab
                        annotations={annotations}
                        summary={annotationSummary}
                        studies={studies}
                        loading={loadingAnnotations}
                        filters={annotationFilters}
                        onFilterChange={setAnnotationFilters}
                        projectId={projectId}
                        onRefresh={fetchAnnotations}
                        canDownload={isAdmin}
                    />
                )}

                {activeTab === "model_runs" && !isViewer && (
                    <ModelRunsSummaryTab
                        runs={modelRuns}
                        summary={modelRunsSummary}
                        studies={studies}
                        loading={loadingModelRuns}
                        filters={modelRunsFilters}
                        onFilterChange={setModelRunsFilters}
                        projectId={projectId}
                        onRefresh={fetchModelRuns}
                    />
                )}

                {activeTab === "reviewer_answers" && !isViewer && (
                    <ReviewerAnswersSummaryTab
                        submissions={reviewerAnswers}
                        summary={reviewerAnswersSummary}
                        studies={studies}
                        loading={loadingReviewerAnswers}
                        filters={reviewerAnswersFilters}
                        onFilterChange={setReviewerAnswersFilters}
                        projectId={projectId}
                        onRefresh={fetchReviewerAnswers}
                    />
                )}
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <ProjectForm
                    project={project}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={() => {
                        setShowEditModal(false);
                        fetchProject();
                    }}
                />
            )}

            {/* Invite Modal */}
            {showInviteModal && (
                <InviteMemberModal
                    projectId={projectId}
                    onClose={() => setShowInviteModal(false)}
                    onSuccess={() => {
                        setShowInviteModal(false);
                        fetchMembers();
                        fetchProject();
                    }}
                />
            )}

            {/* Study Form Modal */}
            {showStudyModal && (
                <StudyForm
                    projectId={projectId}
                    study={editingStudy}
                    onClose={() => {
                        setShowStudyModal(false);
                        setEditingStudy(null);
                    }}
                    onSuccess={() => {
                        setShowStudyModal(false);
                        setEditingStudy(null);
                        fetchStudies();
                        fetchProject();
                    }}
                />
            )}

            {/* Assign Member Modal */}
            {showAssignModal && selectedStudyForAssign && (
                <AssignStudyMemberModal
                    studyId={selectedStudyForAssign.id}
                    projectId={projectId}
                    existingAssignments={selectedStudyForAssign.assignments}
                    onClose={() => {
                        setShowAssignModal(false);
                        setSelectedStudyForAssign(null);
                    }}
                    onSuccess={() => {
                        setShowAssignModal(false);
                        setSelectedStudyForAssign(null);
                        fetchStudies();
                    }}
                />
            )}

            {/* Assign Dataset to Study Modal */}
            {showAssignDatasetModal && selectedDatasetForAssign && (
                <AssignDatasetToStudyModal
                    dataset={selectedDatasetForAssign}
                    studies={studies}
                    onClose={() => {
                        setShowAssignDatasetModal(false);
                        setSelectedDatasetForAssign(null);
                    }}
                    onSuccess={() => {
                        setShowAssignDatasetModal(false);
                        setSelectedDatasetForAssign(null);
                        fetchStudies();
                        fetchDatasets();
                    }}
                />
            )}

            {/* Assign Model to Study Modal */}
            {showAssignModelModal && selectedModelForAssign && (
                <AssignModelToStudyModal
                    model={selectedModelForAssign}
                    studies={studies}
                    onClose={() => {
                        setShowAssignModelModal(false);
                        setSelectedModelForAssign(null);
                    }}
                    onSuccess={() => {
                        setShowAssignModelModal(false);
                        setSelectedModelForAssign(null);
                        fetchStudies();
                        fetchModels();
                    }}
                />
            )}

            {/* Add Existing Dataset Modal */}
            <AddExistingDatasetModal
                show={showAddDatasetModal}
                onHide={() => setShowAddDatasetModal(false)}
                projectId={projectId}
                onDatasetAdded={() => {
                    fetchDatasets();
                    fetchProject(); // Refresh project to update dataset_count
                }}
            />

            {/* Add Existing Model Modal */}
            <AddExistingModelModal
                show={showAddModelModal}
                onHide={() => setShowAddModelModal(false)}
                projectId={projectId}
                onModelAdded={() => {
                    fetchModels();
                    toast.success('Model added to project successfully!');
                }}
            />

            {/* Assign Tool to Study Modal */}
            {showAssignToolModal && selectedToolForAssign && (
                <AssignToolToStudyModal
                    tool={selectedToolForAssign}
                    studies={studies}
                    onClose={() => {
                        setShowAssignToolModal(false);
                        setSelectedToolForAssign(null);
                    }}
                    onSuccess={() => {
                        setShowAssignToolModal(false);
                        setSelectedToolForAssign(null);
                        fetchStudies();
                        fetchTools();
                    }}
                />
            )}

            {/* Add Existing Tool Modal */}
            <AddExistingToolModal
                show={showAddToolModal}
                onHide={() => setShowAddToolModal(false)}
                projectId={projectId}
                onToolAdded={() => {
                    fetchTools();
                    fetchProject();
                }}
            />
        </div>
    );
};

export default ProjectDetail;
